import React from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import PageWrapper from "@/components/layout/page-wrapper";
import { useParams } from "react-router";
import { useGetTicketDetail } from "@/api/tickets/get-ticket-detail";
import { formatDate, cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Pencil, RotateCcwSquareIcon } from "lucide-react";
import { useResolveTicket } from "@/api/tickets/resolve-ticket";
import { useUpdateTicket } from "@/api/tickets/update-ticket";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";


// Validation schema for ticket update
const ticketSchema = z.object({
    title: z.string().min(1, "Title is required"),
    description: z.string().min(1, "Description is required"),
});

type TicketFormData = z.infer<typeof ticketSchema>;

export default function TicketDetails() {
    const { id } = useParams();
    const ticketId = parseInt(id || "0");
    const { data: ticketData, isLoading, isError } = useGetTicketDetail(ticketId);

    // Resolve functionality - hooks must be at the top level
    const { mutate: resolveTicket, isPending: isResolving } = useResolveTicket();
    const { mutate: updateTicket, isPending: isUpdating } = useUpdateTicket();
    const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
    const [updateDialogOpen, setUpdateDialogOpen] = useState(false);

    // Form for updating ticket - hooks must be at top level
    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        setValue,
    } = useForm<TicketFormData>({
        resolver: zodResolver(ticketSchema),
        defaultValues: {
            title: "",
            description: "",
        },
    });

    // Set form values when ticket data is loaded
    React.useEffect(() => {
        if (ticketData?.data) {
            setValue('title', ticketData.data.title || '');
            setValue('description', ticketData.data.description || '');
        }
    }, [ticketData?.data, setValue]);

    if (isLoading) {
        return (
            <PageWrapper pageTitle="Ticket Details" currentPage="Ticket Details">
                <div className="mx-auto space-y-6">
                    <Card className="shadow-md border p-6">
                        <h2 className="text-xl font-semibold mb-4">Ticket Details</h2>
                        <div className="space-y-4">
                            <div>
                                <p className="text-sm text-gray-500">Issue Title</p>
                                <Skeleton className="h-6 w-3/4" />
                            </div>

                            <div>
                                <p className="text-sm text-gray-500">Description</p>
                                <Skeleton className="h-20 w-full" />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-sm text-gray-500">Ticket ID</p>
                                    <Skeleton className="h-4 w-1/4" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Status</p>
                                    <Skeleton className="h-6 w-1/4" />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-sm text-gray-500">Created At</p>
                                    <Skeleton className="h-4 w-1/2" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Updated At</p>
                                    <Skeleton className="h-4 w-1/2" />
                                </div>
                            </div>

                            <div>
                                <p className="text-sm text-gray-500">User ID</p>
                                <Skeleton className="h-4 w-full" />
                            </div>
                        </div>
                    </Card>
                </div>
            </PageWrapper>
        );
    }

    if (isError || !ticketData) {
        return (
            <PageWrapper pageTitle="Ticket Details" currentPage="Ticket Details">
                <div className="mx-auto space-y-6">
                    <Card className="shadow-md border p-6">
                        <h2 className="text-xl font-semibold mb-4">Ticket Details</h2>
                        <div className="text-center py-8">
                            <p className="text-red-500">Failed to load ticket details</p>
                        </div>
                    </Card>
                </div>
            </PageWrapper>
        );
    }

    const ticket = ticketData.data;

    const handleResolve = () => {
        resolveTicket({ id: ticket.id }, {
            onSuccess: () => {
                setResolveDialogOpen(false);
            },
            onError: (error) => {
                console.error("Error resolving ticket:", error);
                setResolveDialogOpen(false);
            }
        });
    };

    const handleUpdate = (data: TicketFormData) => {
        updateTicket(
            {
                id: ticket.id,
                payload: {
                    title: data.title,
                    description: data.description,
                }
            },
            {
                onSuccess: () => {
                    reset(); // Reset form after successful submission
                    setUpdateDialogOpen(false); // Close the dialog after successful submission
                },
                onError: (error) => {
                    console.error("Error updating ticket:", error);
                    setUpdateDialogOpen(false);
                }
            }
        );
    };

    return (
        <PageWrapper pageTitle="Ticket Details" currentPage="Ticket Details">
            <div className="mx-auto space-y-6">
                {/* Ticket Details Page */}
                <Card className="shadow-md border p-6">
                    <div className="flex justify-between items-start mb-4">
                        <h2 className="text-xl font-semibold">Ticket Details</h2>
                        <div className="flex space-x-2">
                            <Button onClick={() => setUpdateDialogOpen(true)} disabled={isUpdating}>
                                <Pencil className="mr-2 h-4 w-4" />
                                {isUpdating ? "Updating..." : "Update Ticket"}
                            </Button>
                            {ticket.status === "open" && (
                                <Button onClick={() => setResolveDialogOpen(true)} disabled={isResolving}>
                                    <RotateCcwSquareIcon className="mr-2 h-4 w-4" />
                                    {isResolving ? "Resolving..." : "Resolve Ticket"}
                                </Button>
                            )}
                        </div>
                    </div>
                    <div className="space-y-4">
                        <div>
                            <p className="text-sm text-gray-500">Ticket ID</p>
                            <p className="text-lg font-medium">{ticket.id}</p>
                        </div>

                        <div>
                            <p className="text-sm text-gray-500">Issue Title</p>
                            <p className="text-lg font-medium">{ticket.title}</p>
                        </div>

                        <div>
                            <p className="text-sm text-gray-500">Description</p>
                            <p className="text-base whitespace-pre-line">{ticket.description}</p>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-sm text-gray-500">Status</p>
                                <Badge className={cn(
                                    ticket.status === "open" ? "bg-green-200 text-green-800" :
                                    ticket.status === "resolved" ? "bg-blue-200 text-blue-800" :
                                    ticket.status === "closed" ? "bg-gray-200 text-gray-800" :
                                    "bg-orange-200 text-orange-800"
                                )}>
                                    {ticket.status.charAt(0).toUpperCase() + ticket.status.slice(1)}
                                </Badge>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">User ID</p>
                                <p className="text-base">{ticket.user_id}</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-sm text-gray-500">Created At</p>
                                <p className="text-base">{formatDate(new Date(ticket.created_at))}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Updated At</p>
                                <p className="text-base">{formatDate(new Date(ticket.updated_at))}</p>
                            </div>
                        </div>

                        {ticket.resolved_at && (
                            <div>
                                <p className="text-sm text-gray-500">Resolved At</p>
                                <p className="text-base">{formatDate(new Date(ticket.resolved_at))}</p>
                            </div>
                        )}
                    </div>
                </Card>

                {/* Resolve Ticket Dialog */}
                <AlertDialog open={resolveDialogOpen} onOpenChange={setResolveDialogOpen}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Resolve Ticket</AlertDialogTitle>
                            <AlertDialogDescription>
                                Are you sure you want to resolve this ticket? This action will change the status to resolved.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleResolve();
                                }}
                            >
                                {isResolving ? "Resolving..." : "Resolve"}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>

                {/* Update Ticket Dialog */}
                <Dialog open={updateDialogOpen} onOpenChange={setUpdateDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Update Ticket</DialogTitle>
                        </DialogHeader>

                        <form onSubmit={handleSubmit(handleUpdate)} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="title">Title</Label>
                                <Input id="title" {...register("title")} placeholder="Enter ticket title" />
                                {errors.title && <p className="text-sm text-red-500">{errors.title.message}</p>}
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="description">Description</Label>
                                <Textarea
                                    id="description"
                                    {...register("description")}
                                    placeholder="Enter ticket description"
                                    rows={4}
                                />
                                {errors.description && <p className="text-sm text-red-500">{errors.description.message}</p>}
                            </div>

                            <div className="flex justify-end gap-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => {
                                        reset();
                                        setUpdateDialogOpen(false);
                                    }}
                                    disabled={isUpdating}
                                >
                                    Cancel
                                </Button>
                                <Button type="submit" disabled={isUpdating}>
                                    {isUpdating ? "Updating..." : "Update Ticket"}
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>
        </PageWrapper>
    );
}
